/**
 * Coach/partner sharing page (T16.5 / ISSUE-081).
 *
 * Owner half is Premium-gated (feature=coach_sharing): invite a coach/partner
 * by email, see outgoing links, revoke at any time. Grantee half is open: see
 * who shares with you and open their read-only week.
 *
 * Privacy / ED-safety: the shared view is aggregate-only (trend, logging
 * calendar, target history, weekly summary). Chat and individual food entries
 * are never shared. Copy is descriptive and additive — no judgmental framing.
 */
import type { CoachLink, CoachViewSummary, SharedWeekView } from '@nutians/shared/schemas';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumGate } from '../components/PremiumGate.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import {
  useCoachLinks,
  useCoachViews,
  useCreateCoachLink,
  useRevokeCoachLink,
  useSharedWeek,
} from '../hooks/useSharing.js';

// ── Owner: invite form ──────────────────────────────────────────────────────────

function InviteForm() {
  const { t } = useTranslation('sharing');
  const create = useCreateCoachLink();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    create.mutate(
      { granteeEmail: email.trim(), scope: 'week_summary' },
      { onSuccess: () => setEmail('') },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ink-soft">{t('owner.inviteLabel')}</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          placeholder={t('owner.invitePlaceholder')}
          required
          className="rounded border border-husk px-2 py-1.5 text-sm text-ink"
        />
      </label>
      <button
        type="submit"
        disabled={create.isPending || !email.trim()}
        className="self-start rounded bg-leaf px-4 py-1.5 text-sm text-white disabled:opacity-50"
      >
        {create.isPending ? t('owner.inviteSending') : t('owner.inviteSubmit')}
      </button>
      {create.isSuccess && <p className="text-xs text-leaf">{t('owner.inviteSent')}</p>}
      {create.isError && (
        <p className="text-xs text-clay" role="alert">
          {t('owner.inviteError')}
        </p>
      )}
    </form>
  );
}

function LinkRow({ link }: { link: CoachLink }) {
  const { t } = useTranslation('sharing');
  const revoke = useRevokeCoachLink();
  const statusLabel =
    link.status === 'active'
      ? t('owner.statusActive')
      : link.status === 'revoked'
        ? t('owner.statusRevoked')
        : t('owner.statusPending');

  return (
    <div className="flex items-center justify-between border-b border-husk py-2 text-sm">
      <div className="flex flex-col">
        <span className="text-ink">{link.granteeEmail}</span>
        <span className="text-xs text-ink-soft">{statusLabel}</span>
      </div>
      {link.status !== 'revoked' && (
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('owner.revokeConfirm'))) revoke.mutate(link.id);
          }}
          disabled={revoke.isPending}
          className="text-xs text-clay hover:underline disabled:opacity-50"
        >
          {revoke.isPending ? t('owner.revoking') : t('owner.revoke')}
        </button>
      )}
    </div>
  );
}

function OwnerSection() {
  const { t } = useTranslation('sharing');
  const { data: links } = useCoachLinks();

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-base font-medium text-ink">{t('owner.heading')}</h3>
        <p className="mt-1 text-sm text-ink-soft">{t('owner.intro')}</p>
      </div>
      <InviteForm />
      <div>
        <h4 className="mb-1 text-sm font-medium text-ink">{t('owner.linksHeading')}</h4>
        {links && links.length > 0 ? (
          links.map((l) => <LinkRow key={l.id} link={l} />)
        ) : (
          <p className="text-sm text-ink-soft">{t('owner.empty')}</p>
        )}
      </div>
    </section>
  );
}

// ── Shared week view (grantee reads owner aggregates) ────────────────────────────

function AdherenceGrid({ view }: { view: SharedWeekView }) {
  const { t } = useTranslation('sharing');
  return (
    <div className="flex flex-wrap gap-1" role="img" aria-label={t('week.adherence')}>
      {view.adherenceCalendar.map((d) => (
        <span
          key={d.date}
          title={`${d.date} — ${d.hasLog ? t('week.logged') : t('week.notLogged')}`}
          className={`h-2.5 w-2.5 rounded-sm ${d.hasLog ? 'bg-leaf' : 'bg-husk'}`}
        />
      ))}
    </div>
  );
}

function SharedWeek({ ownerUserId, ownerName }: { ownerUserId: string; ownerName: string | null }) {
  const { t } = useTranslation('sharing');
  const { data: view, isLoading, isError } = useSharedWeek(ownerUserId);

  if (isLoading) return <p className="text-sm text-ink-soft">…</p>;
  if (isError || !view) return <p className="text-sm text-clay">{t('week.noData')}</p>;

  const latestWeight = view.weightTrend[view.weightTrend.length - 1];
  const latestTarget = view.targetHistory[0];

  return (
    <div className="mt-2 flex flex-col gap-4 rounded-lg border border-husk p-3">
      <h4 className="text-sm font-medium text-ink">
        {ownerName ? t('week.title', { name: ownerName }) : t('week.titleGeneric')}
      </h4>

      <section>
        <p className="text-xs font-medium text-ink-soft">{t('week.weightTrend')}</p>
        {latestWeight ? (
          <p className="text-sm text-ink">
            {(latestWeight.ewmaKg ?? latestWeight.valueKg).toFixed(1)} kg
          </p>
        ) : (
          <p className="text-sm text-ink-soft">{t('week.noData')}</p>
        )}
      </section>

      <section>
        <p className="mb-1 text-xs font-medium text-ink-soft">{t('week.adherence')}</p>
        <AdherenceGrid view={view} />
      </section>

      <section>
        <p className="text-xs font-medium text-ink-soft">{t('week.targets')}</p>
        {latestTarget ? (
          <p className="text-sm text-ink">
            {latestTarget.kcal} {t('week.kcal')} · {latestTarget.proteinG} g {t('week.protein')}
          </p>
        ) : (
          <p className="text-sm text-ink-soft">{t('week.noData')}</p>
        )}
      </section>
    </div>
  );
}

function ViewRow({ view }: { view: CoachViewSummary }) {
  const { t } = useTranslation('sharing');
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-husk py-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ink">{view.ownerName ?? view.ownerUserId}</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-leaf hover:underline"
        >
          {open ? t('week.back') : t('grantee.viewWeek')}
        </button>
      </div>
      {open && <SharedWeek ownerUserId={view.ownerUserId} ownerName={view.ownerName} />}
    </div>
  );
}

function GranteeSection() {
  const { t } = useTranslation('sharing');
  const { data: views } = useCoachViews();
  if (!views || views.length === 0) {
    return (
      <section>
        <h3 className="text-base font-medium text-ink">{t('grantee.heading')}</h3>
        <p className="mt-1 text-sm text-ink-soft">{t('grantee.empty')}</p>
      </section>
    );
  }
  return (
    <section>
      <h3 className="text-base font-medium text-ink">{t('grantee.heading')}</h3>
      <p className="mt-1 mb-2 text-sm text-ink-soft">{t('grantee.intro')}</p>
      {views.map((v) => (
        <ViewRow key={v.linkId} view={v} />
      ))}
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

export default function SharingPage() {
  const { t } = useTranslation('sharing');
  const { data: entitlement } = useEntitlement();
  const isPremium = entitlement?.isPremium ?? false;

  return (
    <div className="flex max-w-lg flex-col gap-6">
      <h2 className="text-xl font-medium tracking-tight">{t('title')}</h2>

      {isPremium ? (
        <OwnerSection />
      ) : (
        <PremiumGate
          feature="coach_sharing"
          heading={t('upsell.heading')}
          body={t('upsell.body')}
        />
      )}

      <hr className="border-husk" />

      <GranteeSection />

      <p className="text-xs text-ink-soft">{t('privacyNote')}</p>
    </div>
  );
}
